"use client"

import { cn, SetRequired } from "@/lib/utils";
import { cloneElement, ComponentProps, ReactElement, ReactNode } from "react";
import { ZodError } from "zod";
import { ButtonDeleteX_icon } from "../../components/partials/ButtonsUtil";
import { Input } from "../imported/input";
import { Label } from "../imported/label";
import { MultiSelect } from "./MultiSelect";

interface FormItemProps<ItemDataType> {
    /** the name/id of the control - used for sending to Server */
    id: keyof ItemDataType & string,
    /** display text for label */
    text?: string,
    /** onChange validation change */
    validationCheck?: (...a: any[]) => any,
    /** state zodError to display error if needed */
    zodError: ZodError<any> | undefined,
    /** the data item - used for default value if provided */
    className?: string,
    /** item to ref as type & default values if valid */
    item?: ItemDataType,
}
interface ChildProps {
    id?: string;
    name?: string;
}

/** Input item with label, error & validation.
 * ### props:
 * - id
 * - text?
 * - validationCheck
 * - zodError
 * - item
 */
export function FormInput<ItemDataType>(props: SetRequired<FormItemProps<ItemDataType>, "text" | "validationCheck"> & ComponentProps<typeof Input>) {
    const { id, text, validationCheck, zodError, item, className, ...otherProps } = props
    return <FormItem { ...{ id, text, validationCheck, zodError, item, className } }>
        <Input
            id={ id }
            name={ id }
            placeholder={ `הזן ${text}` }
            defaultValue={ (item && (typeof item[id] == "string" || typeof item[id] == "number")) ? item[id] : "" }
            { ...otherProps }
            onInput={ (e) => {
                validationCheck(e)
                props.onInput && props.onInput(e)
            } }
        />
    </FormItem>
}

/** MultiSelect item with label, error & validation.
 * ### props:
 * - options
 * - placeholder
 * - id
 * - text?
 * - validationCheck
 * - zodError
 * - item
 */
export function FormMultiSelect<ItemDataType>({ options, validationCheck, item, text, zodError, id, ...props }:
    FormItemProps<ItemDataType> & ComponentProps<typeof MultiSelect>) {

    return <FormItemCustom id={ id } text={ text } zodError={ zodError } item={ item }>
        <MultiSelect onChange={ validationCheck } options={ options } { ...props } />
    </FormItemCustom>
}

/** Custom item with optional label, error (manual validation needed validation)
 *  
 * ### props:
 * - id
 * - text?
 * - zodError
 * - item
 */
export function FormItemCustom<ItemDataType>(props: Omit<FormItemProps<ItemDataType>, "validationCheck"> & { children: ReactElement<ChildProps> }) {
    const childrenCopy = cloneElement(props.children, {
        id: props.id,
        name: props.id,
    })
    return <FormItem { ...{ ...props, children: undefined } }>
        { childrenCopy }
    </FormItem>
}

/** simplest form item structure */
function FormItem<ItemDataType>({ id, text, zodError, children: formElement, className }: FormItemProps<ItemDataType> & { children: ReactNode }) {
    return <div className={ cn("flex flex-col gap-0.5", className) }>
        { text && <Label htmlFor={ id }>{ text }:</Label> }
        { formElement }
        { zodError && <Error>{ zodError.errors.find(e => e.path[0] == id)?.message }</Error> }
    </div>
}
function Error({ children }: { children: ReactNode }) {
    if (!children)
        return;

    return <p className="text-red-600 text-xs w-min min-w-full flex flex-row gap-1 items-center rounded transition-colors cursor-help py-0.5 ps-1.5 font-black hover:bg-red-800 hover:text-white">
        <ButtonDeleteX_icon size={ "1.5em" } /> { children }.
    </p>
}
